import { type ReactNode, useMemo } from 'react';

import { Icon, type IconType } from '@/app/components/icons';
import { IconCall } from '@/app/components/icons/communication/icon-call';
import { IconFacebook } from '@/app/components/icons/social-media/icon-facebook';
import { IconLine } from '@/app/components/icons/social-media/icon-line';
import { IconMessenger } from '@/app/components/icons/social-media/icon-messenger';
import { IconCalendarMonth } from '@/app/components/icons/ui-actions/icon-calendar-month';
import { Link } from '@/app/components/navigation/link';
import {
  getDealerFacebook,
  getDealerLine,
  getDealerMessenger,
} from '@/app/components/shared/dealer/utils/mapping-dealer';
import { EVENT_NAME } from '@/app/components/shared/dealer/utils/tracking';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
import { formatTel } from '@/app/utils/format';
import type {
  ContactInfo,
  DealerTel,
} from '@/services/contentstack/models/dealer';

const GROUP_NAME = 'components/shared/dealer/dealer-contact';
interface DealerContactProps {
  className?: string;
  data: ContactInfo;
  selectedVehicle: string;
  showOnlyIcon?: boolean;
}

interface ContactItemProps {
  label: string;
  icon?: IconType;
  children: ReactNode;
  isShowOnlyIcon?: boolean;
  dataTest?: string;
}

interface SocialItemProps extends Omit<ContactItemProps, 'children'> {
  data: {
    link: string;
    name: string;
  };
}

interface PhoneItemProps {
  array: DealerTel[];
  showOnlyIcon?: boolean;
  dataTest?: string;
}

const ContactItem = ({
  label,
  icon,
  dataTest,
  children,
  isShowOnlyIcon = false,
}: ContactItemProps) => {
  return (
    <div
      className={cn({
        'flex gap-x-4': isShowOnlyIcon,
        'grid grid-cols-2 gap-x-10': !isShowOnlyIcon,
      })}
    >
      <div className="flex gap-x-2">
        {!!icon && <Icon icon={icon} className="size-5" />}
        {!isShowOnlyIcon && (
          <span
            className="text-body-1-fixed font-noto-bold text-gray-700"
            {...createDataAttributes({ test: dataTest })}
          >
            {label}
          </span>
        )}
      </div>
      {children}
    </div>
  );
};

const SocialItem = ({
  data,
  label,
  icon,
  isShowOnlyIcon = false,
}: SocialItemProps) => {
  const dataTest = `${label.toLowerCase()}`;
  return (
    <div
      className={cn('', {
        'grid grid-cols-2 items-center gap-x-10': !isShowOnlyIcon,
        'flex items-center gap-x-4': isShowOnlyIcon,
      })}
    >
      <div className="flex gap-x-2">
        {!!icon && <Icon icon={icon} className="size-5" />}
        {!isShowOnlyIcon && (
          <span
            className="text-body-1-fixed font-noto-bold text-gray-700"
            {...createDataAttributes({ test: dataTest })}
          >
            {label}
          </span>
        )}
      </div>

      <Link
        title={data.name}
        theme="light"
        target="_blank"
        href={data.link}
        className={cn('truncate text-gray-700', {
          'text-body-1-fixed': !isShowOnlyIcon,
          'text-body-2-fixed': isShowOnlyIcon,
        })}
        {...createDataAttributes({ test: `${dataTest}-value` })}
        {...createDataGA({
          click: true,
          eventName: EVENT_NAME,
          group: GROUP_NAME,
          url: data.link,
          text: data.name,
        })}
      >
        {data.name}
      </Link>
    </div>
  );
};

const PhoneItem = ({
  array = [],
  showOnlyIcon = false,
  dataTest,
}: PhoneItemProps) => {
  return array.map((item, index) => {
    const stripDash = formatTel(item.tel ?? '', true);
    const phone = `${formatTel(item.tel ?? '')}`;
    const ext = item.ext ? ` ต่อ ${item.ext}` : '';
    return (
      <Link
        key={index}
        theme="light"
        href={`tel:${stripDash}`}
        className={cn('where-span:text-inline-wrap text-gray-700', {
          'text-body-1-fixed': !showOnlyIcon,
          'text-body-2-fixed': showOnlyIcon,
        })}
        {...createDataAttributes({ test: `${dataTest}-value` })}
        {...createDataGA({
          click: true,
          eventName: EVENT_NAME,
          group: GROUP_NAME,
          url: `tel:${stripDash}`,
          text: `${phone} ${ext}`,
        })}
      >
        {phone}
        {!!ext && <span className="pl-1">{ext}</span>}
      </Link>
    );
  });
};

export const DealerContact = ({
  className,
  data,
  selectedVehicle,
  showOnlyIcon = false,
}: DealerContactProps) => {
  const facebookLink = useMemo(() => {
    return getDealerFacebook(data)[selectedVehicle];
  }, [data, selectedVehicle]);

  const messengerLink = useMemo(() => {
    return getDealerMessenger(data)[selectedVehicle];
  }, [data, selectedVehicle]);

  const lineLink = useMemo(() => {
    return getDealerLine(data)[selectedVehicle];
  }, [data, selectedVehicle]);

  return (
    <div
      className={cn('flex flex-col space-y-4', className)}
      {...createDataAttributes({ test: 'dealer-contact' })}
    >
      <ContactItem
        label="โทรศัพท์"
        dataTest="phone"
        icon={<IconCall />}
        isShowOnlyIcon={showOnlyIcon}
      >
        {(data?.main_contact?.length || 0) > 0 ? (
          <div className="flex flex-col">
            <PhoneItem
              array={data?.main_contact}
              showOnlyIcon={showOnlyIcon}
              dataTest="phone"
            />
          </div>
        ) : (
          '-'
        )}
      </ContactItem>

      <ContactItem
        label="นัดหมาย"
        dataTest="appointment"
        icon={<IconCalendarMonth />}
        isShowOnlyIcon={showOnlyIcon}
      >
        {(data?.appointment_contact?.length || 0) > 0 ? (
          <div>
            <PhoneItem
              array={data?.appointment_contact}
              showOnlyIcon={showOnlyIcon}
              dataTest="appointment"
            />
          </div>
        ) : (
          '-'
        )}
      </ContactItem>

      {!!facebookLink?.name && (
        <SocialItem
          data={facebookLink}
          label="Facebook"
          icon={<IconFacebook />}
          isShowOnlyIcon={showOnlyIcon}
        />
      )}

      {!!messengerLink?.name && (
        <SocialItem
          data={messengerLink}
          label="Messenger"
          icon={<IconMessenger />}
          isShowOnlyIcon={showOnlyIcon}
        />
      )}

      {!!lineLink?.name && (
        <SocialItem
          data={lineLink}
          label="LINE"
          icon={<IconLine />}
          isShowOnlyIcon={showOnlyIcon}
        />
      )}
    </div>
  );
};
